import React from 'react';

export function useFocusLastChipOnBackspace(
    chipRefs: React.RefObject<React.RefObject<HTMLElement>[]>,
    inputRef?: React.RefObject<HTMLInputElement>,
) {
    const findPreviousEnabledChip = React.useCallback(
        (startIndex: number | null = null) => {
            const { current: chips } = chipRefs;
            if (!chips) {
                return undefined;
            }
            for (let i = startIndex ?? chips.length - 1; i >= 0; i--) {
                const chip = chips[i]?.current;
                if (chip && chip?.getAttribute('aria-disabled') !== 'true') {
                    return chip;
                }
            }
            return undefined;
        },
        [chipRefs],
    );

    React.useEffect(() => {
        const input = inputRef?.current;
        if (!input) {
            return undefined;
        }

        const onKeyDown = (evt: KeyboardEvent) => {
            const backspacePressed = evt.key === 'Backspace';
            const cursorAtStart = input.selectionStart === 0 && input.selectionEnd === 0;
            if (!backspacePressed || !cursorAtStart) {
                return;
            }

            evt.stopPropagation();
            evt.preventDefault();

            // Focus last (enabled) chip (if any)
            const lastChip = findPreviousEnabledChip();
            lastChip?.focus();
        };

        input.addEventListener('keydown', onKeyDown);
        return () => {
            input.removeEventListener('keydown', onKeyDown);
        };
    }, [inputRef, findPreviousEnabledChip]);

    return { findPreviousEnabledChip };
}
